import { t } from 'i18next';
import { Color } from 'three';

import { createIterator } from './design-node/create-iterator';
import { ValueId } from './skymap-extras';

const gray = [0, 0, 0.5] as const;
const red = [0, 1, 0.5] as const;
const blue = [0.66, 1, 0.5] as const;
const green = [0.3, 1, 0.5] as const;
const yellow = [0.14, 1, 0.5] as const;
const cyan = [0.5, 1, 0.5] as const;
const purple = [0.8, 1, 0.5] as const;

const lightYellow = [0.14, 1, 0.65] as const;
const lightGray = [0, 0, 0.95] as const;
const pink = [0.9, 1, 0.5] as const;
const skyBlue = [0.57, 0.9, 0.58] as const;
const lime = [0.2, 1, 0.5] as const;
const orange = [0.09, 1, 0.5] as const;
const brown = [0.07, 0.45, 0.49] as const;
const jade = [0.43, 1, 0.5] as const;

const defaultFaceColor = new Color(0xcccccc);
const defaultLineSegmentColor = new Color().setHSL(...blue);
const defaultPointColor = new Color().setHSL(...cyan);
const defaultAttributeColor = new Color().setHSL(...purple);

export const sfmViewerColors = {
  cameras: {
    disabled: new Color().setHSL(...lightGray).toArray() as [number, number, number],
    unaligned: new Color().setHSL(...red).toArray() as [number, number, number],
    pending: new Color().setHSL(...gray).toArray() as [number, number, number],
    default: new Color().setHSL(...cyan).toArray() as [number, number, number],
    loaded: new Color().setHSL(...jade).toArray() as [number, number, number],
  },
  gcp: {
    default: new Color().setHSL(...pink).toArray() as [number, number, number],
    disabled: new Color().setHSL(...lightGray).toArray() as [number, number, number],
    checkpoint: new Color().setHSL(...lightYellow).toArray() as [number, number, number],
  },
  cursors: {
    cursor: new Color().setHSL(...yellow).toArray() as [number, number, number],
    gcpCursor: new Color().setHSL(...orange).toArray() as [number, number, number],
    pinnedImageCursors: new Color().setHSL(...green).toArray() as [number, number, number],
    unpinnedImageCursors: new Color().setHSL(...blue).toArray() as [number, number, number],
    otherCursor: new Color().setHSL(...lightGray).toArray() as [number, number, number],
  },
  legends: {
    none: new Color().setHSL(...gray).toArray() as [number, number, number],
    excellent: new Color().setHSL(...green).toArray() as [number, number, number],
    ok: new Color().setHSL(...yellow).toArray() as [number, number, number],
    caution: new Color().setHSL(...orange).toArray() as [number, number, number],
    critical: new Color().setHSL(...red).toArray() as [number, number, number],
  },
};

export const designColors = {
  defaultPointColor,
  defaultLineSegmentColor,
  defaultFaceColor,
  defaultAttributeColor,
  colorPickerHsl: {
    row1: [gray, red, blue, green, yellow, orange, cyan],
    row2: [lightGray, pink, skyBlue, jade, lime, brown, purple],
  },
} as const;

const defaultEnumValueColors = [
  new Color().setHSL(...red),
  new Color().setHSL(...blue),
  new Color().setHSL(...green),
  new Color().setHSL(...yellow),
  new Color().setHSL(...cyan),
  new Color().setHSL(...pink),
  new Color().setHSL(...skyBlue),
  new Color().setHSL(...purple),
  new Color().setHSL(...lime),
  new Color().setHSL(...orange),
  new Color().setHSL(...jade),
];

export function getDefaultEnumValueColor(id: ValueId): Color {
  return defaultEnumValueColors[id % defaultEnumValueColors.length];
}

export function getDefaultColorValueMap() {
  const colorMap = new Map<ValueId, Color>();
  const valueMap = new Map<ValueId, string>();

  const iterator = createIterator<ValueId>();

  const addColor = (name: string, color: Color) => {
    const id = iterator.next();
    colorMap.set(id, color);
    valueMap.set(id, name);
  };

  addColor(t('color.red', { ns: 'common' }) as string, new Color().setHSL(...red));
  addColor(t('color.blue', { ns: 'common' }) as string, new Color().setHSL(...blue));
  addColor(t('color.green', { ns: 'common' }) as string, new Color().setHSL(...green));
  addColor(t('color.cyan', { ns: 'common' }) as string, new Color().setHSL(...cyan));
  addColor(t('color.yellow', { ns: 'common' }) as string, new Color().setHSL(...yellow));
  addColor(t('color.pink', { ns: 'common' }) as string, new Color().setHSL(...pink));
  addColor(t('color.purple', { ns: 'common' }) as string, new Color().setHSL(...purple));
  addColor(t('color.orange', { ns: 'common' }) as string, new Color().setHSL(...orange));
  addColor(t('color.lightGreen', { ns: 'common' }) as string, new Color().setHSL(...lime));
  addColor(t('color.brown', { ns: 'common' }) as string, new Color().setHSL(...brown));
  addColor(t('color.lightBlue', { ns: 'common' }) as string, new Color().setHSL(...skyBlue));

  return { colorMap, valueMap };
}
